import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import BusinessIcon from '@mui/icons-material/Business';
import { layout } from '@/shared/theme/tokens';
import { prefetchRoute, prefetchRoutes, shouldBackgroundPrefetch } from '@/shared/routing/routePrefetch';
import {
  RAIL_ICONS,
  getRailDefaultPath,
  getRailMenuItems,
  isMenuPathActive,
  workspaceMenuItems,
  workspaceRailPath,
  type NavWorkspaceEntry,
  type NavAccessOpts,
  type NavMenuItem,
  type NavRailDef,
  type RailId,
} from '@/shared/layout/nav';

const OPEN_BG = 'rgba(25, 118, 210, 0.08)';

interface NavColumnProps {
  rails: NavRailDef[];
  /** Switcher sits under the brand row. */
  belowBrand?: React.ReactNode;
  platformItems: NavMenuItem[];
  activeRailId: RailId | null;
  onSelectRail: (railId: RailId) => void;
  access: NavAccessOpts;
  /** Active workspace. Pages outside its list stay hidden. */
  workspace?: NavWorkspaceEntry;
  /** Icon-only column. Labels and child rows stay mounted in the expanded column. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

/**
 * Single left nav: logo, module rows, and inline children.
 * A module with one destination navigates immediately.
 * A module with several destinations (and Việc) expands in place.
 */
const NavColumn: React.FC<NavColumnProps> = ({
  rails,
  belowBrand,
  platformItems,
  activeRailId,
  onSelectRail,
  access,
  workspace,
  collapsed,
  onToggleCollapsed,
}) => {
  const { t } = useTranslation('navigation');
  const { t: tCommon } = useTranslation('common');
  const { t: tIdentity } = useTranslation('identity');
  const navigate = useNavigate();
  const location = useLocation();
  const [openIds, setOpenIds] = useState<Set<RailId>>(() =>
    activeRailId ? new Set([activeRailId]) : new Set()
  );

  const itemsByRail = useMemo(() => {
    const map = {} as Record<RailId, NavMenuItem[]>;
    for (const rail of rails) {
      map[rail.id] = workspaceMenuItems(rail.id, getRailMenuItems(rail.id, access), workspace);
    }
    return map;
  }, [access, rails, workspace]);

  useEffect(() => {
    if (!activeRailId) return;
    setOpenIds((prev) => {
      if (prev.has(activeRailId)) return prev;
      const next = new Set(prev);
      next.add(activeRailId);
      return next;
    });
  }, [activeRailId]);

  useEffect(() => {
    if (!shouldBackgroundPrefetch()) return;
    const paths = rails.flatMap((rail) => {
      const items = itemsByRail[rail.id] ?? [];
      const fromItems = items.map((item) => item.path);
      return fromItems;
    });
    paths.push(...platformItems.map((item) => item.path));
    const run = () => prefetchRoutes(paths);
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(run, { timeout: 2000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(run, 400);
    return () => window.clearTimeout(id);
  }, [itemsByRail, platformItems, rails]);

  const isItemActive = (item: NavMenuItem): boolean =>
    isMenuPathActive(location.pathname, location.search, item.path, item.match || 'prefix');

  const toggle = (railId: RailId) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(railId)) next.delete(railId);
      else next.add(railId);
      return next;
    });
  };

  const openRail = (railId: RailId) => {
    setOpenIds((prev) => {
      if (prev.has(railId)) return prev;
      const next = new Set(prev);
      next.add(railId);
      return next;
    });
  };

  const rowSx = (active: boolean) => ({
    minHeight: 40,
    px: collapsed ? 0 : 1.5,
    mx: collapsed ? 0.75 : 1,
    borderRadius: 1,
    justifyContent: collapsed ? 'center' : 'flex-start',
    color: active ? 'primary.main' : 'text.primary',
    bgcolor: active ? OPEN_BG : 'transparent',
    transition: (theme: Theme) =>
      theme.transitions.create(['padding', 'margin', 'background-color'], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
  });

  const iconSx = {
    minWidth: collapsed ? 0 : 32,
    mr: collapsed ? 0 : undefined,
    color: 'inherit',
    justifyContent: 'center',
    transition: (theme: Theme) =>
      theme.transitions.create(['min-width', 'margin'], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
  };

  const labelSx = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    minWidth: 0,
    flex: collapsed ? '0 0 0px' : '1 1 auto',
    maxWidth: collapsed ? 0 : 168,
    opacity: collapsed ? 0 : 1,
    transition: (theme: Theme) =>
      theme.transitions.create(['max-width', 'opacity', 'flex'], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
  };

  const goLeaf = (rail: NavRailDef) => {
    onSelectRail(rail.id);
    if (activeRailId === rail.id) return;
    navigate(workspaceRailPath(rail.id, getRailMenuItems(rail.id, access), getRailDefaultPath(rail.id, access), workspace));
  };

  return (
    <Box
      component="nav"
      aria-label={t('railNav')}
      sx={{
        width: collapsed ? layout.navCollapsedWidth : layout.drawerWidth,
        transition: (theme) =>
          theme.transitions.create('width', {
            duration: theme.transitions.duration.standard,
            easing: theme.transitions.easing.easeInOut,
          }),
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxSizing: 'border-box',
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: 'rgb(248, 248, 248)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          height: layout.appBarHeight,
          display: 'flex',
          alignItems: 'center',
          px: collapsed ? 0 : 2,
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: collapsed ? 0 : 1.25,
          transition: (theme) =>
            theme.transitions.create(['padding', 'gap'], {
              duration: theme.transitions.duration.standard,
              easing: theme.transitions.easing.easeInOut,
            }),
          boxSizing: 'border-box',
        }}
      >
        <Box
          role="img"
          aria-label={tIdentity('brand')}
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            bgcolor: 'primary.main',
            maskImage: 'url(/logo/beetle-logo.png)',
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            WebkitMaskImage: 'url(/logo/beetle-logo.png)',
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
          }}
        />
        <Box
          component="span"
          sx={{
            ...labelSx,
            fontWeight: 700,
            fontSize: '0.95rem',
            letterSpacing: 0.2,
            color: 'text.primary',
            textOverflow: 'ellipsis',
          }}
        >
          {tIdentity('brand')}
        </Box>
      </Box>

      {belowBrand}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', py: 0.5 }}>
        <List dense disablePadding>
          {platformItems.map((item) => {
            const active = isItemActive(item);
            const button = (
              <ListItemButton
                onPointerEnter={() => prefetchRoute(item.path)}
                onPointerDown={() => prefetchRoute(item.path)}
                onClick={() => navigate(item.path)}
                sx={rowSx(active)}
              >
                <ListItemIcon sx={iconSx}>
                  {item.icon ?? <BusinessIcon fontSize="small" />}
                </ListItemIcon>
                <Box sx={labelSx}>
                  <ListItemText
                    primary={t(item.labelKey)}
                    slotProps={{
                      primary: {
                        variant: 'body2',
                        fontWeight: active ? 600 : 500,
                        noWrap: true,
                      },
                    }}
                  />
                </Box>
              </ListItemButton>
            );
            return (
              <Tooltip
                key={item.path}
                title={t(item.labelKey)}
                placement="right"
                disableHoverListener={!collapsed}
                disableFocusListener={!collapsed}
              >
                {button}
              </Tooltip>
            );
          })}

          {rails.map((rail) => {
            const Icon = RAIL_ICONS[rail.id];
            const items = itemsByRail[rail.id] ?? [];
            const expandable = items.length > 1;
            const expanded = openIds.has(rail.id);
            const railActive = activeRailId === rail.id;

            if (!expandable) {
              const button = (
                <ListItemButton
                  aria-current={railActive ? 'page' : undefined}
                  onPointerEnter={() => prefetchRoute(workspaceRailPath(rail.id, getRailMenuItems(rail.id, access), getRailDefaultPath(rail.id, access), workspace))}
                  onPointerDown={() => prefetchRoute(workspaceRailPath(rail.id, getRailMenuItems(rail.id, access), getRailDefaultPath(rail.id, access), workspace))}
                  onClick={() => goLeaf(rail)}
                  sx={rowSx(railActive)}
                >
                  <ListItemIcon sx={iconSx}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <Box sx={labelSx}>
                    <ListItemText
                      primary={t(rail.labelKey)}
                      slotProps={{
                        primary: {
                          variant: 'body2',
                          fontWeight: railActive ? 600 : 500,
                          noWrap: true,
                        },
                      }}
                    />
                  </Box>
                </ListItemButton>
              );
              return (
                <Tooltip
                  key={rail.id}
                  title={t(rail.labelKey)}
                  placement="right"
                  disableHoverListener={!collapsed}
                  disableFocusListener={!collapsed}
                >
                  {button}
                </Tooltip>
              );
            }

            return (
              <Box key={rail.id}>
                <Tooltip
                  title={t(rail.labelKey)}
                  placement="right"
                  disableHoverListener={!collapsed}
                  disableFocusListener={!collapsed}
                >
                  <ListItemButton
                    aria-expanded={expanded && !collapsed}
                    onClick={() => {
                      if (collapsed) {
                        goLeaf(rail);
                        return;
                      }
                      // Header of the current module only folds the list.
                      // Another module must navigate, or the previous one stays highlighted.
                      if (railActive) {
                        toggle(rail.id);
                        return;
                      }
                      openRail(rail.id);
                      goLeaf(rail);
                    }}
                    sx={rowSx(railActive)}
                  >
                    <ListItemIcon sx={iconSx}>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    <Box sx={labelSx}>
                      <ListItemText
                        primary={t(rail.labelKey)}
                        slotProps={{
                          primary: {
                            variant: 'body2',
                            fontWeight: railActive ? 600 : 500,
                            noWrap: true,
                          },
                        }}
                      />
                    </Box>
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        color: 'text.secondary',
                        flexShrink: 0,
                        width: collapsed ? 0 : 20,
                        opacity: collapsed ? 0 : 1,
                        transform: expanded ? 'rotate(180deg)' : 'none',
                        transition: (theme) =>
                          theme.transitions.create(['transform', 'width', 'opacity'], {
                            duration: theme.transitions.duration.standard,
                            easing: theme.transitions.easing.easeInOut,
                          }),
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
                <Collapse in={expanded && !collapsed} timeout={300} unmountOnExit={false}>
                  <List dense disablePadding>
                    {items.map((item) => {
                      const active = isItemActive(item);
                      return (
                        <ListItemButton
                          key={item.labelKey}
                          onPointerEnter={() => prefetchRoute(item.path)}
                          onPointerDown={() => prefetchRoute(item.path)}
                          onClick={() => navigate(item.path)}
                          sx={{
                            minHeight: 34,
                            pl: 6.5,
                            pr: 1.5,
                            mx: 1,
                            borderRadius: 1,
                            color: active ? 'primary.main' : 'text.primary',
                          }}
                        >
                          <ListItemText
                            primary={t(item.labelKey)}
                            slotProps={{
                              primary: {
                                variant: 'body2',
                                fontWeight: active ? 600 : 400,
                              },
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-end',
          px: collapsed ? 0 : 1,
          py: 0.75,
          borderTop: 1,
          borderColor: 'divider',
          transition: (theme) =>
            theme.transitions.create(['padding'], {
              duration: theme.transitions.duration.standard,
              easing: theme.transitions.easing.easeInOut,
            }),
        }}
      >
        <Tooltip title={collapsed ? tCommon('expandNav') : tCommon('collapseNav')} placement="right">
          <IconButton
            size="small"
            aria-label={collapsed ? tCommon('expandNav') : tCommon('collapseNav')}
            aria-expanded={!collapsed}
            onClick={onToggleCollapsed}
          >
            {collapsed ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default NavColumn;
